/** @format */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography, withStyles } from '@material-ui/core';

import { configDefault } from '../../../core/config';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { simCardConstants } from '../../../modules/simcard/actions';
import adescomStyle from '../../../assets/jss/adescom/adescomStyle';
import Filter from '../../../interfaces/forms/Filter';
import Order from '../../../interfaces/forms/Order';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import Pager from '../../../components/Adescom/PagerRange';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import AddButton from '../../../components/Buttons/AddButton';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { FilterListReducerFieldName } from '../../../modules/filter/FilterListReducers';
import { FilterReducerFieldName } from '../../../modules/filter/FilterReducers';
import orange from '@material-ui/core/colors/orange';
import GridItem from '../../../components/Grid/GridItem';
import I18n from '../../../components/Translate/I18n';
import GridContainer from '../../../components/Grid/GridContainer';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import PaperedTable from '../../../components/Table/PaperedTable';

interface BillingItemListProps {
    classes: any;
    selectNumberCallback: (params: any) => void;
    selectedPlatformReseller: number | null;
}

const BillingItemList = (props: BillingItemListProps): React.ReactElement => {
    const [filters, setFilters] = useState<Array<Filter>>([{ name: 'deleted', value: false }]);
    const [orders, setOrders] = useState<Array<Order>>([{ name: 'id', value: 'desc' }]);
    const SIM_CARD_PATH = '/sim_cards';
    const dispatch = useDispatch();
    const classes = props.classes;

    const { type, collections } = useSelector((state: { simCardReducers: any }) => {
        return state.simCardReducers;
    });

    const tableControlStickyHeader = { root: classes.tableControlStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };
    const tableRootNotMinWidth = { root: classes.tableRootNotMinWidth };

    const paginationCallbackFunction = (uri: string | null = null): void => {
        if (!uri) {
            uri = FormGeneralHelper.getPath(SIM_CARD_PATH, configDefault.PLATFORM_API_PREFIX);
        }
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(uri),
            simCardConstants.SIM_CARD_LIST,
            [...filters, {name: 'selectedPlatformReseller', value: props.selectedPlatformReseller ?? -1}],
            orders,
            (data: any) => {
                dispatch({
                    type: simCardConstants.SIM_CARD_LIST,
                    collections: data,
                });
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    };

    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);
    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);

    const clearFilter = (): void => {
        setOrders([{ name: 'id', value: 'desc' }]);
        setFilters([{ name: 'deleted', value: false }]);

        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });
        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
    };

    useEffect(() => {
        if (props.selectedPlatformReseller) {
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/sim_cards', configDefault.PLATFORM_API_PREFIX)),
                simCardConstants.SIM_CARD_LIST,
                [
                    { name: 'deleted', value: false },
                    {name: 'selectedPlatformReseller', value: props.selectedPlatformReseller ?? -1},
                ],
                [{ name: 'id', value: 'desc' }],
                (data: any) => {
                    dispatch({
                        type: simCardConstants.SIM_CARD_LIST,
                        collections: data,
                    });
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        }
    }, [dispatch, props.selectedPlatformReseller]);

    return (
        <>
            <Table classes={tableRootNotMinWidth}>
                <TableHead>
                    <TableRow>
                        <TableCell classes={tableControlStickyHeader} colSpan={3} align="left" padding="none">
                            <div style={{ padding: 15 }}>
                                {(filters.length > 0 || orders.length > 0) && (
                                    <CustomTextButtom
                                        size={'lg'}
                                        callback={clearFilter}
                                        callbackParam={{}}
                                        color="primary"
                                        label="general:common.clear"
                                    />
                                )}
                                <CustomTextButtom callback={paginationCallbackFunction} color="primary" label="general:common.search" size={'lg'} />
                            </div>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell classes={tableControlStickyHeader} padding="none" align="left">
                            <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true}/>
                        </TableCell>
                        <TableCell classes={tableControlStickyHeader} align="left" padding="none">
                            <TableHeaderContentCell
                                textAlign="left"
                                title="pools:pool.msisdn"
                                fieldName="msisdnAll"
                                showSort={true}
                                onlyFilter={false}
                                defaultDirection="desc"
                                searchFieldType="string"
                                updateFilterList={updateFilterList}
                                updateOrderList={updateOrderList}
                            />
                        </TableCell>
                        <TableCell classes={tableControlStickyHeader} align="left" padding="none">
                            <TableHeaderContentCell
                                textAlign="left"
                                title="pools:pool.imsi"
                                fieldName="imsi"
                                showSort={true}
                                onlyFilter={false}
                                defaultDirection="desc"
                                searchFieldType="string"
                                updateFilterList={updateFilterList}
                                updateOrderList={updateOrderList}
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {collections &&
                        collections['hydra:member'].map((row: any) => {
                            let style = {};
                            if (row.lastMsisdn) {
                                style = { backgroundColor: orange[100] };
                            }
                            return (
                                <TableRow key={row.id} hover style={style}>
                                    <TableCell classes={tableCellRoot} align={'left'}>
                                        <AddButton callback={props.selectNumberCallback} callbackParam={{ number: row.lastMsisdn ?? row.msisdn }} />
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} align={'left'}>
                                        {row.lastMsisdn ?? row.msisdn}
                                    </TableCell>
                                    <TableCell classes={tableCellRoot} align={'left'}>
                                        {row.imsi}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                </TableBody>
                <TableFooter>
                    {collections && (
                        <TableRow>
                            <TableCell colSpan={11}>
                                <GridContainer style={{ marginLeft: '20px', marginBottom: '20px' }}>
                                    <GridItem xs={12} md={12} lg={12}>
                                        <GridContainer>
                                            <GridItem xs={12}>
                                                <Typography variant="subtitle2">
                                                    <I18n trans="prov:list.legend" />
                                                </Typography>
                                            </GridItem>
                                            <GridItem xs={1} style={{ backgroundColor: orange['A100'] }} />
                                            <GridItem xs={11}>
                                                <I18n trans="simcard:list.deleted" />
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </TableCell>
                        </TableRow>
                    )}
                </TableFooter>
                <TableFooter>
                    <TableCell classes={tableCellRoot} colSpan={3}>
                        {collections && collections['hydra:view'] && <Pager callback={paginationCallbackFunction} views={collections} />}
                    </TableCell>
                </TableFooter>
            </Table>
        </>
    );
};

export default withStyles(oplAppStyle)(BillingItemList);
